import React, { useState } from 'react';
import { Shield, AlertTriangle, CheckCircle, Copy, Download, Sparkles, FileText, Save, Trash2, Zap, Mail } from 'lucide-react';
import { analyzeContract, quickScan, generateNegotiationEmail, type AnalysisResult } from './lib/engine';
import { aiDeepAnalysis } from './lib/ai-fallback';
import { SettingsButton } from './lib/SettingsButton';

interface SavedAnalysis { id: string; name: string; text: string; preview: string; result: AnalysisResult; date: string; }

export default function App() {
  const [contractText, setContractText] = useState('');
  const [result, setResult] = useState<AnalysisResult | null>(null);
  const [quickResult, setQuickResult] = useState<ReturnType<typeof quickScan>>([]);
  const [aiAnalysis, setAiAnalysis] = useState('');
  const [isAiLoading, setIsAiLoading] = useState(false);
  const [saved, setSaved] = useState<SavedAnalysis[]>(() => JSON.parse(localStorage.getItem('ca_contracts') || '[]'));
  const [viewMode, setViewMode] = useState<'full' | 'quick'>('full');
  const [showEmail, setShowEmail] = useState(false);

  const analyze = () => {
    if (!contractText.trim()) return;
    const res = analyzeContract(contractText);
    const quick = quickScan(contractText);
    setResult(res);
    setQuickResult(quick);
    setAiAnalysis('');
    setShowEmail(false);
  };

  const handleAiDeep = async () => {
    if (!contractText.trim()) return;
    setIsAiLoading(true); setAiAnalysis('');
    await aiDeepAnalysis(contractText, c => setAiAnalysis(p => p + c));
    setIsAiLoading(false);
  };

  const saveAnalysis = () => {
    if (!result) return;
    const item: SavedAnalysis = { id: Date.now().toString(), name: `Contract — ${new Date().toLocaleDateString()}`, text: contractText, preview: contractText.slice(0, 100) + (contractText.length > 100 ? '…' : ''), result, date: new Date().toLocaleDateString() };
    const n = [item, ...saved]; setSaved(n); localStorage.setItem('ca_contracts', JSON.stringify(n));
  };

  const loadAnalysis = (item: SavedAnalysis) => {
    setContractText(item.text);
    setResult(item.result);
    setQuickResult(quickScan(item.text));
    setAiAnalysis('');
  };

  const riskColor = (r: string) => r === 'CRITICAL' ? 'text-red-400' : r === 'HIGH' ? 'text-orange-400' : 'text-yellow-400';
  const riskBg = (r: string) => r === 'CRITICAL' ? 'bg-red-500/10 border-red-500/30' : r === 'HIGH' ? 'bg-orange-500/10 border-orange-500/30' : 'bg-yellow-500/10 border-yellow-500/30';
  const riskBadge = (r: string) => r === 'CRITICAL' ? 'bg-red-500 text-white' : r === 'HIGH' ? 'bg-orange-500 text-white' : 'bg-yellow-500 text-black';
  const overallColor = (s: number) => s >= 80 ? 'text-green-400' : s >= 60 ? 'text-yellow-400' : s >= 40 ? 'text-orange-400' : 'text-red-400';

  return (
    <div className="min-h-screen bg-[#050b14] text-[#f0f0f0]">
      <header className="border-b border-white/10 bg-[#0a1628] sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-[#d4af37] flex items-center justify-center text-[#0a1628] font-bold text-lg">C</div>
            <div>
              <h1 className="text-lg font-semibold tracking-tight text-[#f0f0f0]">CatalystAgile <span className="text-[#d4af37]">Contract Risk Analyzer</span></h1>
              <p className="text-xs text-[#8a9bb8]">Clause Database + Missing Protection Detector + AI Analysis</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setViewMode(viewMode === 'full' ? 'quick' : 'full')} className="px-3 py-1.5 text-xs rounded-md bg-[#132238] hover:bg-[#1a2d4a] border border-white/10 text-[#8a9bb8] transition-colors">
              {viewMode === 'full' ? 'Quick Scan' : 'Full Analysis'}
            </button>
            {result && (
              <>
                <button onClick={() => setShowEmail(!showEmail)} className="px-3 py-1.5 text-xs rounded-md bg-[#132238] hover:bg-[#1a2d4a] border border-white/10 text-[#8a9bb8] transition-colors flex items-center gap-1.5"><Mail className="w-3.5 h-3.5"/> Email</button>
                <button onClick={saveAnalysis} className="px-3 py-1.5 text-xs rounded-md bg-[#d4af37] text-[#0a1628] font-semibold hover:bg-[#e8c547] transition-colors flex items-center gap-1.5"><Save className="w-3.5 h-3.5"/> Save</button>
              </>
            )}
            <SettingsButton className="px-3 py-1.5 text-xs rounded-md bg-[#132238] hover:bg-[#1a2d4a] border border-white/10 inline-flex items-center gap-1.5 transition-colors text-[#8a9bb8] hover:text-[#d4af37]" />
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-6 grid grid-cols-1 lg:grid-cols-12 gap-6">
        <aside className="lg:col-span-3 space-y-3">
          <div className="bg-[#0f1d32] rounded-xl border border-white/10 p-4">
            <h2 className="text-sm font-semibold text-[#d4af37] mb-3 flex items-center gap-2"><FileText className="w-4 h-4"/> Contract Input</h2>
            <textarea value={contractText} onChange={e => setContractText(e.target.value)} rows={12}
              placeholder="Paste your contract clause or full contract here..."
              className="w-full bg-[#070f1a] border border-white/10 rounded-lg p-3 text-sm text-[#f0f0f0] placeholder-[#8a9bb8]/50 focus:outline-none focus:border-[#d4af37]/50 resize-none font-mono mb-3"/>
            <div className="flex gap-2">
              <button onClick={analyze} className="flex-1 px-4 py-2.5 rounded-lg bg-[#d4af37] text-[#0a1628] font-semibold text-sm hover:bg-[#e8c547] transition-colors flex items-center justify-center gap-1.5">
                <Shield className="w-4 h-4"/> Analyze
              </button>
              <button onClick={handleAiDeep} disabled={isAiLoading} className="px-3 py-2.5 rounded-lg bg-[#132238] border border-[#d4af37]/30 text-[#d4af37] text-sm hover:bg-[#d4af37]/10 transition-colors disabled:opacity-50 flex items-center gap-1.5">
                <Sparkles className="w-4 h-4"/> {isAiLoading ? '...' : 'AI'}
              </button>
            </div>
          </div>

          <div className="bg-[#0f1d32] rounded-xl border border-white/10 p-4">
            <h2 className="text-sm font-semibold text-[#d4af37] mb-3 flex items-center gap-2"><Save className="w-4 h-4"/> Saved ({saved.length})</h2>
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {saved.map(item => (
                <button key={item.id} onClick={() => loadAnalysis(item)} className="w-full text-left bg-[#132238] rounded-lg p-2.5 border border-white/5 hover:border-[#d4af37]/30 transition-colors">
                  <div className="text-xs font-medium text-[#f0f0f0]">{item.name}</div>
                  <div className="flex items-center gap-2 mt-1">
                    <span className={`text-[10px] px-1.5 py-0.5 rounded font-bold uppercase ${riskBadge(item.result.overallRisk)}`}>{item.result.overallRisk}</span>
                    <span className="text-[10px] text-[#8a9bb8]">{item.result.score}/100 · {item.date}</span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </aside>

        <section className="lg:col-span-9 space-y-4">
          {/* Quick Scan Results */}
          {viewMode === 'quick' && quickResult.length > 0 && (
            <div className="bg-[#0f1d32] rounded-xl border border-white/10 p-4">
              <h2 className="text-sm font-semibold text-[#f0f0f0] mb-3 flex items-center gap-2"><Zap className="w-4 h-4 text-[#d4af37]"/> Quick Scan — Top 5 Risks</h2>
              <div className="space-y-2">
                {quickResult.map((f, i) => (
                  <div key={i} className={`p-3 rounded-lg border ${riskBg(f.risk)}`}>
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`text-[10px] px-1.5 py-0.5 rounded font-bold uppercase ${riskBadge(f.risk)}`}>{f.risk}</span>
                      <span className="text-xs text-[#f0f0f0] font-medium capitalize">{f.category.replace(/_/g, ' ')}</span>
                    </div>
                    <p className="text-xs text-[#8a9bb8]">{f.explanation}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Full Analysis */}
          {viewMode === 'full' && result && (
            <>
              {/* Score */}
              <div className="bg-[#0f1d32] rounded-xl border border-white/10 p-6 text-center">
                <div className={`text-5xl font-bold mb-2 ${overallColor(result.score)}`}>{result.score}<span className="text-lg text-[#8a9bb8]">/100</span></div>
                <div className={`inline-block px-4 py-1.5 rounded-lg text-sm font-semibold ${riskBg(result.overallRisk)} ${riskColor(result.overallRisk)}`}>{result.overallRisk} RISK</div>
                <div className="mt-4 h-2 bg-[#132238] rounded-full overflow-hidden">
                  <div className="h-full rounded-full transition-all duration-500" style={{ width: `${result.score}%`, backgroundColor: result.score >= 80 ? '#22c55e' : result.score >= 60 ? '#f59e0b' : result.score >= 40 ? '#f97316' : '#ef4444' }}/>
                </div>
              </div>

              {/* Flagged Clauses */}
              {result.flaggedClauses.length > 0 && (
                <div className="bg-[#0f1d32] rounded-xl border border-white/10 p-4">
                  <h2 className="text-sm font-semibold text-[#f0f0f0] mb-3 flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-red-400"/> Flagged Clauses ({result.flaggedClauses.length})</h2>
                  <div className="space-y-3">
                    {result.flaggedClauses.map((f, i) => (
                      <div key={i} className={`p-4 rounded-lg border ${riskBg(f.risk)}`}>
                        <div className="flex items-center gap-2 mb-2">
                          <span className={`text-[10px] px-1.5 py-0.5 rounded font-bold uppercase ${riskBadge(f.risk)}`}>{f.risk}</span>
                          <span className="text-xs font-medium text-[#f0f0f0] capitalize">{f.category.replace(/_/g, ' ')}</span>
                        </div>
                        <div className="bg-[#070f1a] rounded p-2 mb-2 border border-white/5">
                          <p className="text-xs text-[#8a9bb8] font-mono">"{f.text}"</p>
                        </div>
                        <p className="text-xs text-[#f0f0f0] mb-2">{f.explanation}</p>
                        <div className="bg-[#070f1a] rounded p-2 border border-[#d4af37]/10 mb-2">
                          <p className="text-[10px] text-[#d4af37] uppercase tracking-wider mb-1">Suggested Alternative</p>
                          <p className="text-xs text-[#f0f0f0] font-mono">{f.standardAlternative}</p>
                        </div>
                        <button onClick={() => navigator.clipboard.writeText(f.standardAlternative)} className="text-[10px] text-[#8a9bb8] hover:text-[#d4af37] flex items-center gap-1 transition-colors"><Copy className="w-3 h-3"/> Copy Fix</button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Missing Protections */}
              {result.missingProtections.length > 0 && (
                <div className="bg-[#0f1d32] rounded-xl border border-white/10 p-4">
                  <h2 className="text-sm font-semibold text-[#f0f0f0] mb-3 flex items-center gap-2"><CheckCircle className="w-4 h-4 text-yellow-400"/> Missing Protections ({result.missingProtections.length})</h2>
                  <div className="space-y-2">
                    {result.missingProtections.map((m, i) => (
                      <div key={i} className="flex items-start gap-3 bg-[#132238] rounded-lg p-3 border border-white/5">
                        <span className={`text-[10px] px-1.5 py-0.5 rounded font-bold uppercase shrink-0 ${m.importance === 'CRITICAL' ? 'bg-red-500 text-white' : 'bg-yellow-500 text-black'}`}>{m.importance}</span>
                        <div>
                          <p className="text-xs text-[#f0f0f0] font-medium capitalize">{m.protection}</p>
                          <p className="text-xs text-[#8a9bb8] mt-0.5">{m.suggestion}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Negotiation Email */}
              {showEmail && result.flaggedClauses.length > 0 && (
                <div className="bg-[#0f1d32] rounded-xl border border-[#d4af37]/20 p-4">
                  <h2 className="text-sm font-semibold text-[#d4af37] mb-3 flex items-center gap-2"><Mail className="w-4 h-4"/> Negotiation Email</h2>
                  <pre className="bg-[#070f1a] rounded-lg p-4 text-xs text-[#f0f0f0] font-mono whitespace-pre-wrap border border-white/10">{generateNegotiationEmail(result.flaggedClauses)}</pre>
                  <button onClick={() => navigator.clipboard.writeText(generateNegotiationEmail(result.flaggedClauses))} className="mt-2 text-xs text-[#8a9bb8] hover:text-[#d4af37] flex items-center gap-1 transition-colors"><Copy className="w-3.5 h-3.5"/> Copy Email</button>
                </div>
              )}
            </>
          )}

          {/* AI Deep Analysis */}
          {aiAnalysis && (
            <div className="bg-[#0f1d32] rounded-xl border border-[#d4af37]/20 p-4">
              <h2 className="text-sm font-semibold text-[#d4af37] mb-3 flex items-center gap-2"><Sparkles className="w-4 h-4"/> AI Deep Analysis</h2>
              <div className="bg-[#070f1a] rounded-lg p-4 text-sm text-[#f0f0f0] whitespace-pre-wrap border border-white/10">{aiAnalysis}</div>
            </div>
          )}

          {!result && !aiAnalysis && (
            <div className="bg-[#0f1d32] rounded-xl border border-white/10 p-12 text-center">
              <Shield className="w-12 h-12 text-[#8a9bb8] mx-auto mb-4"/>
              <h2 className="text-lg font-semibold text-[#f0f0f0] mb-2">Analyze a Contract</h2>
              <p className="text-sm text-[#8a9bb8]">Paste a contract or clause on the left and click Analyze to detect dangerous language and missing protections.</p>
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
